import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  DatePicker,
  Select,
  Space,
  Tooltip,
  Switch,
  Typography,
  Badge,
  Upload as UploadAntd,
  Avatar,
  Image,
} from "antd";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { getExampleNumber } from "libphonenumber-js/max";
import examples from "libphonenumber-js/examples.mobile.json";
import {
  EyeOutlined,
  PlusOutlined,
  UserOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import lang from "../helper/lang";
import { Eye, Pencil, Trash2, Lock } from "lucide-react";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import SuspendIcon from "../assets/images/suspend-main-icon.svg";
import CheckImgIcon from "../assets/images/activee-main-icon.svg";
import apiPath from "../constants/apiPath";
import SingleImageUpload from "../components/SingleImageUpload";
import notfound from "../assets/images/not_found.png";
import PhoneInput from "react-phone-input-2";
import moment from "moment";

const { Search } = Input;
const { Title, Text } = Typography;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

function UserManager() {
  const { request } = useRequest();

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [image, setImage] = useState();
  const [modalType, setModalType] = useState("add");
  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const FileType = ["image/png", "image/jpg", "image/jpeg", "image/webp"];
  const [filters, setFilters] = useState({
    status: null,
    sort: null,
    dateRange: null,
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Loading states for different operations
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [statusToggleLoading, setStatusToggleLoading] = useState({});
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const debouncedSearchText = useDebounce(searchText, 500);
  const isInitialRender = useRef(true);
  const [phoneCountry, setPhoneCountry] = useState(null);
  const [phoneCountryIso, setPhoneCountryIso] = useState(null);

  // Helper function to map API status to boolean
  const mapStatusToBoolean = (status) => {
    return status === "Active" || status === "active";
  };

  // Fetch users
  const fetchUsers = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.status === true) {
      params.append("status", "Active");
    } else if (filters.status === false) {
      params.append("status", "Inactive");
    } else {
      params.append("status", "");
    }
    if (filters.sort) {
      params.append("sort", filters.sort);
    }
    if (filters.dateRange?.length === 2) {
      try {
        // RangePicker returns dayjs objects which have format method
        params.append("start_date", filters.dateRange[0].format("YYYY-MM-DD"));
        params.append("end_date", filters.dateRange[1].format("YYYY-MM-DD"));
      } catch (e) {
        console.error("Date formatting error:", e);
        // Fallback: try to get date string in different ways
        try {
          if (
            filters.dateRange[0] &&
            typeof filters.dateRange[0].toISOString === "function"
          ) {
            params.append(
              "start_date",
              filters.dateRange[0].toISOString().split("T")[0],
            );
            params.append(
              "end_date",
              filters.dateRange[1].toISOString().split("T")[0],
            );
          } else if (
            filters.dateRange[0] &&
            typeof filters.dateRange[0].toDate === "function"
          ) {
            // If it's a dayjs object with toDate method
            params.append(
              "startDate",
              filters.dateRange[0].toDate().toISOString().split("T")[0],
            );
            params.append(
              "endDate",
              filters.dateRange[1].toDate().toISOString().split("T")[0],
            );
          }
        } catch (fallbackError) {
          console.error("Fallback date formatting also failed:", fallbackError);
        }
      }
    }
    if (debouncedSearchText) params.append("search", debouncedSearchText);
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    request({
      url: `/api/admin/users?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        let usersData = [];
        let totalCount = 0;
        let currentPageFromApi = undefined;
        let limitFromApi = undefined;

        if (data?.data) {
          // Common shapes: { data: { users, pagination } } or { data: { docs, totalDocs } }
          if (Array.isArray(data.data)) {
            usersData = data.data;
          }

          const meta = data.data.pagination || data.pagination || {};
          totalCount =
            meta.totalCount ?? meta.total ?? data.data.totalDocs ?? 0;
          currentPageFromApi = meta.currentPage ?? meta.page ?? meta.pageNumber;
          limitFromApi = meta.limit ?? meta.pageSize;
        } else if (Array.isArray(data)) {
          usersData = data;
          totalCount = data.length;
        }

        setUsers(Array.isArray(usersData) ? usersData : []);
        setPagination((prev) => ({
          ...prev,
          total: totalCount,
          current: currentPageFromApi ?? prev.current,
          pageSize: limitFromApi ?? prev.pageSize,
        }));
      },
      onError: (error) => {
        setLoading(false);
        setUsers([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        ShowToast(
          `${
            error.message ||
            error.response.data.message ||
            error.response.message ||
            "Failed to terminate session"
          }`,
          Severty.ERROR,
        );
      },
    });
  }, [
    filters,
    debouncedSearchText,
    pagination.current,
    pagination.pageSize,
    request,
  ]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    fetchUsers();
  }, [filters, debouncedSearchText, pagination.current, pagination.pageSize]);

  // Handlers
  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleRemoveImage = () => {
    setImage(null); // clear image preview
    form.setFieldsValue({ image: null }); // clear form field
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAddUser = () => {
    setModalType("add");
    form.resetFields();

    // 🔥 reset phone state
    setPhoneCountry(null);
    setCountryCode("");
    setImage(null);

    setModalVisible(true);
  };

  const isValidPhoneNumber = (phone, countryCode) => {
    if (!phone || !countryCode) return false;

    const phoneNumber = parsePhoneNumberFromString(
      phone,
      countryCode.toUpperCase()
    );

    return phoneNumber ? phoneNumber.isValid() : false;
  };

  const getPhoneExample = (countryCode) => {
    console.log("country_code==>", countryCode)
    try {
      const example = getExampleNumber(countryCode, examples);
      if (!example) return null;

      const intl = example.formatInternational(); 
      // "+91 62654 53127"

      // keep + and spaces, mask digits except country code
      const countryCallingCode = example.countryCallingCode;

      let seenCountryCode = false;

      return intl.replace(/\d+/g, (match) => {
        // first numeric chunk is country calling code
        if (!seenCountryCode) {
          seenCountryCode = true;
          return match; // keep country code
        }
        return "x".repeat(match.length);
      });
    } catch {
      return null;
    }
  };

  // const getExpectedLocalPhoneLength = (country) => {
  //   console.log("country==>", country)
  //   if (!country?.format || !country?.dialCode) return null;

  //   // total digits represented by dots
  //   const totalDigits = (country.format.match(/\./g) || []).length;
  //   console.log("Total Digits==>", totalDigits)

  //   // subtract country code digits
  //   const countryCodeLength = country.dialCode.length;
  //   console.log("countryCodeLength==>", countryCodeLength)

  //   const localDigits = totalDigits - countryCodeLength;
  //   console.log("Local Digits==>", localDigits)

  //   return localDigits > 0 ? localDigits : null;
  // };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setModalType("edit");
    setModalVisible(true);
    form.setFieldsValue({
      full_name: user.full_name,
      username: user.username || "",
      email: user.email,
      mobile: `${user.country_code}${user.mobile}` || user.phone,
      isActive: user.status === "Active" ? true : false,

      pronoun: user.pronoun,
      gender: user.gender,
      blood_group: user.blood_group,
      date_of_birth: user.date_of_birth
        ? moment(user.date_of_birth)
        : null,
    });
    setCountryCode(user.country_code || "");
    setImage(user.profile_img || null);
  };

  const handleViewProfile = (user) => {
    setSelectedUser(user);
    setModalType("view");
    setModalVisible(true);
  };

  const handleImage = (data) => {
    data.length > 0 ? setImage(data?.[0]?.url) : setImage("");
  };

  const PRONOUN_OPTIONS = [
    "Dr.", "Mr.", "Ms.", "Mrs."
  ].map(pr => ({label: pr, value: pr}));

  const GENDER_OPTIONS = [
    { label: "Male", value: "MALE" },
    { label: "Female", value: "FEMALE" },
    { label: "Other", value: "OTHERS" },
  ];

  const BLOOD_GROUP_OPTIONS = [
    "A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-",
  ].map(bg => ({ label: bg, value: bg }));

  //   ------------ USER Create and Update ----------
  const handleFormSubmit = async (values) => {
    try {
      const userData = {
        full_name: values.full_name,
        // username: values.username,
        user_type: "USER",
        email: values.email,
        mobile: values.mobile.slice(countryCode.length),
        country_code: countryCode,
        profile_img: image,
        status: values.isActive === true ? "Active" : "Inactive",
        pronoun: values.pronoun,
        gender: values.gender,
        blood_group: values.blood_group,
        date_of_birth: values.date_of_birth
          ? values.date_of_birth.format("YYYY-MM-DD")
          : null,
      };

      // Add password only when creating a new user
      if (modalType === "add" && values.password) {
        userData.password = values.password;
      }

      if (modalType === "add") {
        setCreateLoading(true);
        request({
          url: `/api/admin/users`,
          method: "POST",
          data: userData,
          onSuccess: () => {
            ShowToast("User created successfully", Severty.SUCCESS);
            setImage(null); // ✅ explicitly null
            form.resetFields();
            setModalVisible(false);
            fetchUsers();
            setCreateLoading(false);
            setPhoneCountryIso(null);
          },
          onError: (error) => {
            console.log("error=====>", error);
            console.log("error_response=====>", error.response);
            ShowToast(
              error?.response?.data?.message || "Failed to update user",
              Severty.ERROR,
            );
            setModalVisible(false);
            setCreateLoading(false);
          },
        });
      } else {
        setUpdateLoading(true);
        request({
          url: `/api/admin/users/${selectedUser._id || selectedUser.id}`,
          method: "PUT",
          data: userData,
          onSuccess: () => {
            ShowToast("User updated successfully", Severty.SUCCESS);
            setModalVisible(false);
            fetchUsers();
            setUpdateLoading(false);
          },
          onError: (error) => {
            ShowToast(
              error?.response?.data?.message || "Failed to update user",
              Severty.ERROR,
            );
            setUpdateLoading(false);
          },
        });
      }
    } catch (error) {
      ShowToast("Failed to save user", Severty.ERROR);
      setCreateLoading(false);
      setUpdateLoading(false);
    }
  };

  const resetUserModalState = () => {
    setModalVisible(false);
    form.resetFields();

    // 🔥 THIS IS THE KEY
    setPhoneCountry(null);
    setPhoneCountryIso(null);
    setCountryCode("");
    setImage(null);
  };

  const handleDeleteUser = async (user, reason) => {
    try {
      const userId = user._id || user.id;
      setDeleteLoading((prev) => ({ ...prev, [userId]: true }));
      request({
        url: `/api/admin/users/${userId}`,
        method: "DELETE",
        data: { reason: reason },
        onSuccess: (data) => {
          console.log("data-=-=-=-=->", data);
          ShowToast("User deleted successfully", Severty.SUCCESS);
          setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
          fetchUsers();
        },
        onError: (error) => {
          ShowToast(
            `${
              error.message ||
              error.response.data.message ||
              error.response.message ||
              "Failed to terminate session"
            }`,
            Severty.ERROR,
          );
          setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
        },
      });
    } catch (error) {
      ShowToast(
        `${
          error.message ||
          error.response.data.message ||
          error.response.message ||
          "Failed to terminate session"
        }`,
        Severty.ERROR,
      );
      const userId = user._id || user.id;
      setDeleteLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const resetDeleteModal = () => {
    setDeleteModalVisible(false);
    setUserToDelete(null);
  };

  const handleStatusToggle = async (user, newStatus) => {
    try {
      const userId = user._id || user.id;
      setStatusToggleLoading((prev) => ({ ...prev, [userId]: true }));
      let params = new URLSearchParams();
      params.append("action", newStatus === true ? "active" : "inactive");
      request({
        url: `/api/admin/users/${userId}?${params.toString()}`,
        method: "DELETE",
        onSuccess: () => {
          ShowToast(
            `User ${newStatus ? "activated" : "suspended"} successfully`,
            Severty.SUCCESS,
          );
          fetchUsers();
          setStatusToggleLoading((prev) => ({ ...prev, [userId]: false }));
        },
        onError: (error) => {
          ShowToast(
            `${
              error.message ||
              error.response.data.message ||
              error.response.message ||
              "Failed to terminate session"
            }`,
            Severty.ERROR,
          );
          setStatusToggleLoading((prev) => ({ ...prev, [userId]: false }));
        },
      });
    } catch (error) {
      ShowToast("Failed to update user status", Severty.ERROR);
      const userId = user._id || user.id;
      setStatusToggleLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleUpdateUserPassword = async (values) => {
    try {
      setUpdateLoading(true);

      const payload = {
        user_id: selectedUser.id,
        new_password: values.password,
        confirm_new_password: values.confirm_password,
      };
      // Reset the password with the new password entered by the user
      request({
        url: "/auth/change-password",
        method: "POST",
        data: payload,
        onSuccess: (data) => {
          ShowToast("Password has been changed successfully", Severty.SUCCESS);

          form2.resetFields();
          setChangePasswordModal(false);
          setUpdateLoading(false);
        },
        onError: (error) => {
          ShowToast(
            `${
              error.message ||
              error.response.data.message ||
              error.response.message ||
              "Failed to terminate session"
            }`,
            Severty.ERROR,
          );
          setUpdateLoading(false);
        },
      });
    } catch (error) {
      ShowToast(
        `${
          error.message ||
          error.response.data.message ||
          error.response.message ||
          "Failed to terminate session"
        }`,
        Severty.ERROR,
      );
      setUpdateLoading(false);
      console.log("Error occured while update password", error);
    }
  };

  // Table columns
  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: "User ID",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (id) => <Text code>USER_{id}</Text>,
    },
    {
      title: "Name",
      dataIndex: "full_name",
      key: "name",
      width: 200,
      sorter: (a, b) => {
        const nameA = a?.full_name?.toLowerCase() || "";
        const nameB = b?.full_name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      },
      render: (name, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Avatar
            icon={<UserOutlined />}
            src={record?.profile_img || record?.user?.profile_img || record?.user?.avatar}
            style={{ backgroundColor: "#1890ff" }}
          />
          <span>{name || record?.user?.full_name || "N/A"}</span>
        </div>
      ),
    },
    {
      title: lang("Email") || "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
      render: (email) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{email || "N/A"}</span>
        </div>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "mobile",
      key: "mobile",
      width: 200,
      render: (mobile, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{`${record.country_code}-${mobile}` || "N/A"}</span>
        </div>
      ),
    },
    {
      title: lang("Status") || "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) => {
        const isActive = mapStatusToBoolean(status || record.isActive);
        return (
          <Tag color={isActive ? "green" : "red"}>
            {status || (record.isActive ? "Active" : "In Active")}
          </Tag>
        );
      },
      onFilter: (value, record) =>
        mapStatusToBoolean(record.status || record.is_active) === value,
      sorter: (a, b) => {
        const statusA = mapStatusToBoolean(a.status || a.isActive);
        const statusB = mapStatusToBoolean(b.status || b.isActive);
        return statusA - statusB;
      },
    },
    {
      title: "Registration Date",
      dataIndex: "join_date",
      key: "join_date",
      width: 120,
      sorter: (a, b) =>
        moment(a.join_date).valueOf() - moment(b.join_date).valueOf(),
      render: (date) => (date ? moment(date).format("DD MMM YYYY") : "-"),
      // render: (join_date) => {
      //   return (
      //     <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      //       <span>{join_date || "N/A"}</span>
      //     </div>
      //   );
      // },
      // onFilter: (value, record) =>
      //   mapStatusToBoolean(record.status || record.is_active) === value,
      // sorter: (a, b) => {
      //   const statusA = mapStatusToBoolean(a.status || a.isActive);
      //   const statusB = mapStatusToBoolean(b.status || b.isActive);
      //   return statusA - statusB;
      // },
    },
    {
      title: lang("Actions") || "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={lang("View_Profile") || "View Profile"}>
            <Button
              className="button-action-small"
              icon={<Eye />}
              onClick={() => handleViewProfile(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>
          <Tooltip title={lang("Edit") || "Edit"}>
            <Button
              className="button-action-small"
              icon={<Pencil />}
              onClick={() => handleEditUser(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>
          <Tooltip title={"Change Password"}>
            <Button
              className="button-action-small"
              icon={<Lock />}
              onClick={() => {
                setSelectedUser(record);
                setChangePasswordModal(true);
              }}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>
          {mapStatusToBoolean(record.status || record.isActive) ? (
            <Tooltip title={"In Active"}>
              <Button
                className="button-action-small"
                icon={<img src={SuspendIcon} alt="inactive" />}
                style={{ color: "#faad14" }}
                loading={statusToggleLoading[record._id || record.id]}
                disabled={
                  statusToggleLoading[record._id || record.id] ||
                  deleteLoading[record._id || record.id]
                }
                onClick={() =>
                  showUserStatusConfirm(
                    record,
                    true,
                    handleStatusToggle,
                    lang,
                    statusToggleLoading,
                    deleteLoading,
                  )
                }
              />
            </Tooltip>
          ) : (
            <Tooltip title={lang("Activate") || "Activate"}>
              <Button
                className="button-action-small"
                icon={<img src={CheckImgIcon} alt="activate" />}
                style={{ color: "#52c41a" }}
                loading={statusToggleLoading[record._id || record.id]}
                disabled={
                  statusToggleLoading[record._id || record.id] ||
                  deleteLoading[record._id || record.id]
                }
                onClick={() =>
                  showUserStatusConfirm(
                    record,
                    false,
                    handleStatusToggle,
                    lang,
                    statusToggleLoading,
                    deleteLoading,
                  )
                }
              />
            </Tooltip>
          )}
          <Tooltip title={lang("Delete") || "Delete"}>
            <Button
              className="button-action-small"
              icon={<Trash2 />}
              style={{ color: "#ff4d4f" }}
              loading={deleteLoading[record._id || record.id]}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
              onClick={() => {
                setUserToDelete(record);
                setDeleteModalVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const showUserStatusConfirm = (
    record,
    isActive,
    handleStatusToggle,
    lang,
    statusToggleLoading,
    deleteLoading,
  ) => {
    const loading = statusToggleLoading[record._id || record.id];
    const disabled = loading || deleteLoading[record._id || record.id];

    confirm({
      title: isActive
        ? "Are you sure you want to in-active this user?"
        : "Are you sure you want to activate this user?",
      okText: lang("Yes") || "Yes",
      cancelText: lang("No") || "No",
      okType: isActive ? "primary" : "primary",
      centered: true,
      okButtonProps: {
        loading,
        disabled,
      },
      onOk() {
        handleStatusToggle(record, !isActive);
      },
    });
  };

  return (
    <div>
      <Card
        title={
          <>
            <TeamOutlined style={{ marginRight: 8 }} />
            {lang("User_Management") || "User Management"}
            <p>
              {" "}
              {lang(
                "Admin_manages_end_user_accounts_view_edit_suspend_or_delete_as_necessary",
              ) ||
                "Admin manages end-user accounts: view, edit, suspend or delete as necessary."}
            </p>
          </>
        }
        extra={
          <>
            <Search
              placeholder={
                lang("Search_by_name_or_email") || "Search by name or email"
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              style={{ width: "100%" }}
            />
            <Select
              placeholder={lang("Filter_by_Status") || "Filter by Status"}
              style={{ width: "100%" }}
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
            >
              <Select.Option value={true}>{"Active"}</Select.Option>
              <Select.Option value={false}>{"In Active"}</Select.Option>
            </Select>
            <Select
              placeholder={"Sorting"}
              style={{ width: "100%" }}
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange("sort", value)}
            >
              <Select.Option value={"name"}>{"Name Wise"}</Select.Option>
              <Select.Option value={"status"}>{"Status Wise"}</Select.Option>
              <Select.Option value={"newest"}>{"Newest User"}</Select.Option>
              <Select.Option value={"oldest"}>{"Oldest User"}</Select.Option>
            </Select>
            <RangePicker
              style={{ width: "100%" }}
              placeholder={[
                lang("Start_Date") || "Start Date",
                lang("End_Date") || "End Date",
              ]}
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange("dateRange", dates)}
            />
            <Button
              icon={<ExclamationCircleOutlined />}
              onClick={() => {
                setFilters({
                  status: null,
                  subscriptionStatus: null,
                  dateRange: null,
                });
                setSearchText("");
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            >
              {lang("Clear_Filters") || "Clear Filters"}
            </Button>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddUser}
            >
              {lang("Add_User") || "Add User"}
            </Button>
          </>
        }
      >
        {/* Users Table */}
        <div className="table-responsive">
          <Table
            columns={getColumns()}
            dataSource={Array.isArray(users) ? users : []}
            rowKey="_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total || 0,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${lang("Showing") || "Showing"} ${range[0]}-${range[1]} ${
                  lang("of") || "of"
                } ${total} ${lang("users") || "users"}`,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </Card>

      {/* Add/Edit User Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <UserOutlined style={{ color: "#1890ff" }} />
            {modalType === "add"
              ? lang("Add_User") || "Add User"
              : lang("Edit_User") || "Edit User"}
          </div>
        }
        open={modalVisible && modalType !== "view"}
        onCancel={() =>
          !createLoading && !updateLoading && setModalVisible(false)
        }
        footer={null}
        width={700}
        confirmLoading={createLoading || updateLoading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => handleFormSubmit(values)}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="full_name"
                label={"Name"}
                rules={[
                  {
                    required: true,
                    pattern: /^[A-Za-z0-9 _-]+$/,
                    message: "Special characters are not allowed",
                  },
                  // {
                  //   required: true,
                  //   message: "Please enter user full name",
                  // },
                  {
                    validator: (_, value) => {
                      if (value.trim() === "") {
                        return Promise.reject(
                          new Error("Name cannot be empty or whitespace"),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                  {
                    max: 100,
                    message:
                      lang("Name_must_be_100_characters_or_less") ||
                      "Name must be 100 characters or less",
                  },
                ]}
              >
                <Input placeholder={"Enter user full name"} />
              </Form.Item>
            </Col>
            <Col span={12}>
            <Form.Item
              name="pronoun"
              label="Pronoun"
              rules={[{ required: true, message: "Please select pronoun" }]}
            >
              <Select placeholder="Select pronoun" options={PRONOUN_OPTIONS} />
            </Form.Item>
              {/* <Form.Item
                name="username"
                label={"Username"}
                rules={[
                  {
                    type: "text",
                    message: "Please enter username",
                  },
                  {
                    pattern: /^[A-Za-z0-9 _-]+$/,
                    message: "Special characters are not allowed",
                  },
                ]}
              >
                <Input placeholder={"Enter username"} />
              </Form.Item> */}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="gender"
                label="Gender"
              >
                <Select placeholder="Select gender" options={GENDER_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="blood_group"
                label="Blood Group"
                rules={[{ required: true, message: "Please select blood group" }]}
              >
                <Select
                  placeholder="Select blood group"
                  options={BLOOD_GROUP_OPTIONS}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label={lang("Email") || "Email"}
                rules={[
                  { required: true, message: "Please enter email" },
                  {
                    validator(_, value) {
                      if (!value) return Promise.resolve();

                      // Trim spaces
                      const email = value.trim();

                      // ❌ basic structure
                      if (!email.includes("@")) {
                        return Promise.reject(
                          new Error("Invalid email format"),
                        );
                      }

                      const [local, domain] = email.split("@");

                      // ❌ local part checks
                      if (
                        !local ||
                        local.startsWith(".") ||
                        local.endsWith(".")
                      ) {
                        return Promise.reject(
                          new Error("Invalid email local part"),
                        );
                      }

                      // ❌ domain checks
                      if (
                        !domain ||
                        domain.startsWith(".") ||
                        domain.endsWith(".") ||
                        domain.includes("..")
                      ) {
                        return Promise.reject(
                          new Error("Invalid email domain"),
                        );
                      }

                      // ❌ allowed characters (RFC-safe)
                      const emailRegex =
                        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,24}$/;

                      if (!emailRegex.test(email)) {
                        return Promise.reject(
                          new Error("Invalid email address"),
                        );
                      }

                      // ❌ TLD sanity check
                      const domainParts = domain.split(".");
                      const tld = domainParts[domainParts.length - 1];

                      if (tld.length < 2 || tld.length > 24) {
                        return Promise.reject(
                          new Error("Invalid email extension"),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder={lang("Enter_email") || "Enter email"} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="mobile"
                label="Mobile Number"
                help={
                  phoneCountryIso
                    ? `Example: ${getPhoneExample(phoneCountryIso)}`
                    : null
                }
                rules={[
                  { required: true, message: "Please enter your phone number" },
                  () => ({
                    validator(_, value) {
                      if (!value || !phoneCountryIso) return Promise.resolve();

                      // remove non-digits
                      const digitsOnly = value.replace(/\D/g, "");
                      const localNumber = digitsOnly.slice(
                        phoneCountry.dialCode.length,
                      );

                      // Minimum sanity check
                      if (!/^\d+$/.test(localNumber)) {
                        return Promise.reject(
                          new Error("Phone number must contain only digits"),
                        );
                      }

                      // const expectedLength =
                      //   getExpectedLocalLength(phoneCountry);

                      const valid = isValidPhoneNumber(value, phoneCountryIso);

                      return valid
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error("Invalid phone number for selected country")
                          );
                    },
                  }),
                ]}
              >
                <PhoneInput
                  country="in"
                  enableSearch
                  value={`${form.getFieldValue("mobile")}`}
                  onChange={(value, country) => {
                    form.setFieldsValue({ mobile: value });
                    setCountryCode(country.dialCode);
                    setPhoneCountry(country);
                    setPhoneCountryIso(country.countryCode.toUpperCase());
                  }}
                  inputStyle={{
                    width: "100%",
                    height: "49px",
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          {modalType === "add" && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="password"
                  label={lang("Password") || "Password"}
                  rules={[
                    {
                      required: true,
                      message: "Please enter password",
                    },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();

                        if (value.trim() === "") {
                          return Promise.reject(
                            new Error("Password cannot be empty or whitespace"),
                          );
                        }

                        const passwordRegex =
                          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;

                        if (!passwordRegex.test(value)) {
                          return Promise.reject(
                            new Error(
                              "Password must contain at least 8 characters, including uppercase, lowercase, number and special character",
                            ),
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input.Password
                    placeholder={lang("Enter_password") || "Enter password"}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="confirm_password"
                  label={lang("Confirm_Password") || "Confirm Password"}
                  dependencies={["password"]}
                  rules={[
                    {
                      required: true,
                      message: "Please confirm password",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords do not match"),
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    // prefix={<LockOutlined />}
                    placeholder={lang("Confirm_password") || "Confirm password"}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={24} md={12}>
              <Form.Item
                className=""
                label="Upload Image"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value !== undefined && value?.length > 0) {
                        return Promise.resolve();
                      }
                      // return Promise.reject(new Error(lang("Image is required")));
                      return Promise.resolve();
                    },
                  },
                ]}
                name="image"
              >
                <SingleImageUpload
                  value={image}
                  fileType={FileType}
                  imageType={"Profile"}
                  btnName={"Image"}
                  onChange={(data) => handleImage(data)}
                  isDimension={false}
                />
              </Form.Item>
              <p className="img-size-details">
                You can select only image, pdf format files upto 5 MB file size
              </p>
              {image && (
                <div
                  className="mt-2 add-img-product"
                  style={{ display: "flex", gap: 12, alignItems: "center" }}
                >
                  <Image width={120} src={image ?? notfound} />

                  {modalType === "edit" && (
                    <Button danger size="small" onClick={handleRemoveImage}>
                      Remove Image
                    </Button>
                  )}
                </div>
              )}
            </Col>
            <Col span={12}>
              <Form.Item
                name="date_of_birth"
                label="Date of Birth"
                rules={[{ required: true, message: "Please select date of birth" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={(current) =>
                    current && current > moment().endOf("day")
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="isActive"
            label={"Status"}
            valuePropName="checked"
            initialValue={true}
          >
            <Switch
              checkedChildren={"Active"}
              unCheckedChildren={"In Activate"}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={resetUserModalState}
              disabled={createLoading || updateLoading}
            >
              {lang("Cancel") || "Cancel"}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={modalType === "add" ? createLoading : updateLoading}
              disabled={createLoading || updateLoading}
            >
              {modalType === "add"
                ? lang("Create") || "Create"
                : lang("Update") || "Update"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete User Modal */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        key={userToDelete?.id || userToDelete?._id || "delete-modal"}
        onCancel={resetDeleteModal}
        onConfirm={(reason) => {
          handleDeleteUser(userToDelete, reason);
          resetDeleteModal();
        }}
        title={"Delete User"}
        message={"Are you sure you want to delete this user ?"}
        itemName={userToDelete?.name || userToDelete?.fullName}
        loading={
          userToDelete && deleteLoading[userToDelete._id || userToDelete.id]
        }
        okText={lang("Delete") || "Delete"}
        cancelText={lang("Cancel") || "Cancel"}
        input={true}
      />
      {/* View User Profile Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <EyeOutlined style={{ color: "#1890ff" }} />
            {lang("User_Profile") || "User Profile"}
          </div>
        }
        open={modalType === "view" && modalVisible}
        onCancel={() => {
          if (!createLoading && !updateLoading) {
            resetUserModalState();
          }
        }}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            {lang("Close") || "Close"}
          </Button>,
          selectedUser && (
            <Button
              key="edit"
              type="primary"
              onClick={() => {
                setModalType("edit");
                form.setFieldsValue({
                  full_name: selectedUser.full_name || selectedUser.name,
                  email: selectedUser.email,
                  mobile: selectedUser.mobile || selectedUser.phone,
                  isActive: mapStatusToBoolean(
                    selectedUser.status || selectedUser.isActive,
                  ),
                });
              }}
              disabled={createLoading || updateLoading}
            >
              {lang("Edit") || "Edit"}
            </Button>
          ),
        ]}
        width={800}
      >
        {selectedUser && (
          <div>
            {/* User Header */}
            <Card
              style={{
                marginBottom: 24,
                background: "linear-gradient(135deg, #2c3e50 0%, #4b6cb7 100%)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "12px",
              }}
              bodyStyle={{ padding: "24px" }}
            >
              <Row gutter={[16, 16]} align="middle">
                <Col span={16}>
                  <Title level={2} style={{ color: "#fff", margin: 0 }}>
                    {selectedUser.full_name || selectedUser.fullName || "N/A"}
                  </Title>
                  <Title
                    level={4}
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      margin: "8px 0 0 0",
                    }}
                  >
                    {selectedUser.email || "N/A"}
                  </Title>
                </Col>

                <Col span={8} style={{ textAlign: "right" }}>
                  <Avatar
                    size={64}
                    icon={<UserOutlined />}
                    src={selectedUser.profile_img || selectedUser.profileImage}
                    style={{
                      backgroundColor: "rgba(255,255,255,0.15)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                    }}
                  />
                </Col>
              </Row>
            </Card>

            {/* Info + Account */}
            <Row gutter={[24, 24]}>
              {/* Basic Info */}
              <Col span={12}>
                <Card
                  title={
                    <span style={{ color: "#000" }}>
                      {lang("Basic_Information") || "Basic Information"}
                    </span>
                  }
                  size="small"
                  style={{
                    background: "#1e1e2f",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "10px",
                  }}
                  headStyle={{
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: "#bbb" }}>
                        {lang("User_ID") || "User ID"}:{" "}
                      </Text>
                      <Text code>{selectedUser._id || selectedUser.id}</Text>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: "#bbb" }}>
                        Pronoun:{" "}
                      </Text>
                      <Text style={{ color: "#eee" }}>
                        {selectedUser.pronoun || "N/A"}
                      </Text>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: "#bbb" }}>
                        {lang("Name") || "Name"}:{" "}
                      </Text>
                      <Text style={{ color: "#eee" }}>
                        {selectedUser.full_name || selectedUser.name || "N/A"}
                      </Text>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: "#bbb" }}>
                        Gender:{" "}
                      </Text>
                      <Text style={{ color: "#eee" }}>
                        {selectedUser.gender || "N/A"}
                      </Text>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: "#bbb" }}>
                        {lang("Email") || "Email"}:{" "}
                      </Text>
                      <Text style={{ color: "#eee" }}>
                        {selectedUser.email || "N/A"}
                      </Text>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: "#bbb" }}>
                        {lang("Phone") || "Phone"}:{" "}
                      </Text>
                      <Text style={{ color: "#eee" }}>
                        {selectedUser
                          ? `${selectedUser.country_code || ""} ${selectedUser.mobile || ""}`.trim()
                          : "N/A"}
                      </Text>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: "#bbb" }}>
                        Blood Group:{" "}
                      </Text>
                      <Text style={{ color: "#eee" }}>
                        {selectedUser.blood_group || "N/A"}
                      </Text>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: "#bbb" }}>
                        Date of Birth:{" "}
                      </Text>
                      <Text style={{ color: "#eee" }}>
                        {selectedUser.date_of_birth
                          ? moment(selectedUser.date_of_birth).format("DD MMM YYYY")
                          : "N/A"}
                      </Text>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: "#bbb" }}>
                        Age:{" "}
                      </Text>
                      <Text style={{ color: "#eee" }}>
                        {selectedUser.age ?? "N/A"}
                      </Text>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: "#bbb" }}>
                        {lang("Status") || "Status"}:{" "}
                      </Text>
                      <Badge
                        status={
                          mapStatusToBoolean(
                            selectedUser.status || selectedUser.isActive,
                          )
                            ? "success"
                            : "error"
                        }
                        text={
                          selectedUser.status ||
                          (selectedUser.isActive ? "Active" : "Inactive")
                        }
                      />
                    </div>
                  </div>
                </Card>
              </Col>

              {/* Account Details */}
              <Col span={12}>
                <Card
                  title={
                    <span style={{ color: "#000" }}>
                      {lang("Account_Details") || "Account Details"}
                    </span>
                  }
                  size="small"
                  style={{
                    background: "#1e1e2f",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "10px",
                  }}
                  headStyle={{
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: "#bbb" }}>
                        {"Join Date"}:{" "}
                      </Text>
                      <Text style={{ color: "#eee" }}>
                        {selectedUser.join_date}
                      </Text>
                    </div>

                    <div>
                      <Text strong style={{ color: "#bbb" }}>
                        {lang("Last_Login") || "Last Login"}:{" "}
                      </Text>
                      <Text style={{ color: "#eee" }}>
                        {selectedUser.last_login
                          ? new Date(
                              selectedUser.last_login,
                            ).toLocaleDateString()
                          : "Never"}
                      </Text>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Update Password Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <UserOutlined style={{ color: "#1890ff" }} />
            Update User Password
          </div>
        }
        open={changePasswordModal}
        onCancel={() => setChangePasswordModal(false)}
        footer={null}
        width={700}
        confirmLoading={createLoading || updateLoading}
      >
        <Form
          form={form2}
          layout="vertical"
          onFinish={(values) => handleUpdateUserPassword(values)}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="password"
                label={lang("Password") || "Password"}
                rules={[
                  {
                    required: true,
                    message: "Please enter password",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();

                      if (value.trim() === "") {
                        return Promise.reject(
                          new Error("Password cannot be empty or whitespace"),
                        );
                      }

                      const passwordRegex =
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;

                      if (!passwordRegex.test(value)) {
                        return Promise.reject(
                          new Error(
                            "Password must contain at least 8 characters, including uppercase, lowercase, number and special character",
                          ),
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input.Password
                  // prefix={<LockOutlined />}
                  placeholder={lang("Enter_password") || "Enter password"}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="confirm_password"
                label={lang("Confirm_Password") || "Confirm Password"}
                dependencies={["password"]}
                rules={[
                  {
                    required: true,
                    message: "Please confirm password",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match"),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  // prefix={<LockOutlined />}
                  placeholder={lang("Confirm_password") || "Confirm password"}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Button
              style={{ marginRight: 8 }}
              onClick={() => {
                form2.resetFields();
                setChangePasswordModal(false);
              }}
              disabled={createLoading || updateLoading}
            >
              {lang("Cancel") || "Cancel"}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateLoading}
              disabled={updateLoading}
            >
              {updateLoading ? "Updating..." : "Update Password"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default UserManager;
